'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import { TabsList } from '@/core/presentation/components/ui/Tabs';

export interface AppTabsToolbarProps {
  /** `aria-label` do grupo de abas (acessibilidade). */
  listAriaLabel: string;
  /** Filhos do `TabsList` — em geral vários `TabsTrigger`. */
  children: React.ReactNode;
  /** Conteúdo à direita em `sm+` quando `splitWhen` (ex.: filtro / select). */
  end?: React.ReactNode;
  /**
   * Quando `true`, em `sm+` o toolbar vira linha com lista à esquerda e `end` à direita
   * (padrão usado no dashboard com o seletor de ano).
   */
  splitWhen?: boolean;
  /** Classes na raiz (flex col/row do toolbar). */
  className?: string;
  /** Wrapper só em volta do `TabsList`. */
  listWrapClassName?: string;
  /** Wrapper do slot `end`. */
  endWrapClassName?: string;
  /** Classes extras no `TabsList` (além de `w-full`). */
  tabsListClassName?: string;
}

/**
 * Barra de abas reutilizável: `TabsList` + triggers + slot opcional à direita no desktop.
 * Deve ficar **dentro** de `<Tabs>` (mesmo contrato do Radix).
 */
export function AppTabsToolbar({
  listAriaLabel,
  children,
  end,
  splitWhen = false,
  className,
  listWrapClassName,
  endWrapClassName,
  tabsListClassName,
}: AppTabsToolbarProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-2',
        splitWhen && 'sm:flex-row sm:items-center sm:justify-between sm:gap-3',
        className
      )}
    >
      <div className={cn('min-w-0 w-full', splitWhen && 'sm:flex-1 sm:max-w-md', listWrapClassName)}>
        <TabsList className={cn('w-full', tabsListClassName)} aria-label={listAriaLabel}>
          {children}
        </TabsList>
      </div>
      {end != null ?
        <div
          className={cn(
            'w-full shrink-0 sm:w-auto sm:min-w-[220px] sm:max-w-[min(100%,280px)]',
            endWrapClassName
          )}
        >
          {end}
        </div>
      : null}
    </div>
  );
}

/** Classes base para `TabsContent` em seções do app (dashboard, lembretes, etc.). */
export function appTabsSectionContentClassName(className?: string) {
  return cn('mt-4 focus-visible:ring-offset-white min-w-0', className);
}
